import { forwardRef, useEffect, useImperativeHandle } from "react";
import ArrestInformation from "./ArrestInformation";
import ChargesInformation from "./ChargesInformation";
import ClassificationInformation from "./ClassificationInformation";
import DetentionInformation from "./DetentionInformation";
import ReleaseInformation from "./ReleaseInformation";
import S3Information from "./S3Information";
import { AddDetentionContainer } from "./styles";
import { Form } from "antd";
import dayjs from "dayjs";
import DetentionsBioForm from "./DetentionsBioForm";
import { getDayjsObject, isNonEmptyVariable } from "@/util/common_function";

export const MODE = { NEW: "NEW", EDIT: "EDIT" };

const AddDetention = forwardRef(
  ({ formData, mode = MODE.NEW, formRef, bookingsFieldsEnabled, onFormChange, disabled }) => {
    const [form] = Form.useForm();
  const arrestReport = formData?.detentionsArrestReports?.[0] || {};
  const bio = formData?.detentionsBio?.[0] || {};
  const charges = formData?.detentionsCharges || [];

    useEffect(() => {
      const arrestReport = formData?.detentionsArrestReports?.[0] || {};
      const incarcerationDateTime = arrestReport.incarcerationDate && arrestReport.incarcerationTime
        ? dayjs(`${arrestReport.incarcerationDate} ${arrestReport.incarcerationTime}`, "MM/DD/YYYY HH:mm")
        : null;
        
      const releaseDateTime = arrestReport.releaseDate && arrestReport.releaseTime
        ? dayjs(`${arrestReport.releaseDate} ${arrestReport.releaseTime}`, "MM/DD/YYYY HH:mm")
        : null;
        const classificationEffectiveDate = isNonEmptyVariable(arrestReport?.classificationEffectiveDate)
        ? getDayjsObject(arrestReport.classificationEffectiveDate)
        : null;
      form.setFieldsValue({
        ...formData,
        detentionsArrestReports: [
          {
            ...arrestReport,
            incarcerationDateTime,
            releaseDateTime,
            classificationEffectiveDate,
          },
        ],
      });
    }, [formData]);

    useImperativeHandle(formRef, () => ({
      getFormValues: () => form.getFieldsValue(true),
    }));

    const normalizeArrestReportValues = (values) => {
      const updated = { ...values };
      const arrestReports = Array.isArray(updated.detentionsArrestReports)
        ? [...updated.detentionsArrestReports]
        : [];

      if (arrestReports[0]) {
        const report = { ...arrestReports[0] };

        if (report.incarcerationDateTime) {
          const dt = dayjs(report.incarcerationDateTime);
          if (dt.isValid()) {
            report.incarcerationDate = dt.format("MM/DD/YYYY");
            report.incarcerationTime = dt.format("HH:mm");
          }
        }

        if (report.releaseDateTime) {
          const dt = dayjs(report.releaseDateTime);
          if (dt.isValid()) {
            report.releaseDate = dt.format("MM/DD/YYYY");
            report.releaseTime = dt.format("HH:mm");
          }
        }

        if (report.classificationEffectiveDate) {
          const dt = dayjs(report.classificationEffectiveDate);
          if (dt.isValid()) {
            report.classificationEffectiveDate = dt.format("MM/DD/YYYY");
          }
        }

        arrestReports[0] = report;
        updated.detentionsArrestReports = arrestReports;
      }

      return updated;
    };

    const buildPayload = (values) => {
      const normalized = normalizeArrestReportValues(values);
      const payload = JSON.parse(JSON.stringify(normalized));
      const arrestReportsPayload = payload.detentionsArrestReports || [];

      if (arrestReportsPayload[0] && arrestReportsPayload[0].classificationEffectiveDate) {
        const date = arrestReportsPayload[0].classificationEffectiveDate;
        const dayjsDate = dayjs(date);
        arrestReportsPayload[0].classificationEffectiveDate = dayjsDate.isValid()
          ? dayjsDate.format("MM/DD/YYYY")
          : null;
      }

      return payload;
    };

    const notifyFormChange = (values) => {
      if (!onFormChange) return;
      onFormChange(buildPayload(values));
    };

    const handleBioFormChange = (updatedBio = {}) => {
      const existingBio = form.getFieldValue(["detentionsBio", 0]) || bio || {};
      const mergedBio = { ...existingBio, ...updatedBio };
      const detentionsBio = [mergedBio];

      form.setFieldsValue({ detentionsBio });
      notifyFormChange({ ...form.getFieldsValue(true), detentionsBio });
    };

    return (
      <AddDetentionContainer>
        <Form
          form={form}
          initialValues={{
            ...formData,
            detentionsArrestReports: [
              {
                ...arrestReport,
                incarcerationDateTime: arrestReport.incarcerationDate && arrestReport.incarcerationTime
                  ? dayjs(`${arrestReport.incarcerationDate} ${arrestReport.incarcerationTime}`, "MM/DD/YYYY HH:mm")
                  : null,
                releaseDateTime: arrestReport.releaseDate && arrestReport.releaseTime
                  ? dayjs(`${arrestReport.releaseDate} ${arrestReport.releaseTime}`, "MM/DD/YYYY HH:mm")
                  : null,
                classificationId: isNonEmptyVariable(arrestReport?.classificationId)
                  ? arrestReport.classificationId
                  : 154550003,
                classificationEffectiveDate: isNonEmptyVariable(arrestReport?.classificationEffectiveDate)
                  ? getDayjsObject(arrestReport.classificationEffectiveDate)
                  : null,
              },
            ],
          }}
          disabled={!bookingsFieldsEnabled}
          onValuesChange={(_, all) => {
            notifyFormChange(all);
          }}
        >
          <DetentionsBioForm bio={bio} disabled={!!disabled} onUpdateForm={handleBioFormChange} />
          <DetentionInformation arrestReport={arrestReport} form={form} disabled={!!disabled} />
          <ArrestInformation formData={arrestReport} disabled={!!disabled} />
          <S3Information formData={arrestReport} disabled={!!disabled} />
          <ReleaseInformation arrestReport={arrestReport} form={form} disabled={!!disabled} />
          <ChargesInformation
            formData={charges}
            disabled={!!disabled}
            onChargesChange={(updatedCharges) => {
              form.setFieldsValue({ detentionsCharges: updatedCharges });
              notifyFormChange({ ...form.getFieldsValue(true), detentionsCharges: updatedCharges });
            }}
          />
          <ClassificationInformation formData={arrestReport} disabled={!!disabled} />
        </Form>
      </AddDetentionContainer>
    );
  }
);

export default AddDetention;
